import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TicketList from './components/TicketList';
import CreateTicketModal from './components/CreateTicketModal';
import TicketDetailsModal from './components/TicketDetailsModal';
import TicketFilters from './components/TicketFilters';
import TicketStats from './components/TicketStats';
import AIInsightsPanel from './components/AIInsightsPanel';
import { supportTicketService } from '../../services/supportTicketService';

const AISupportTicketSystem = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, isTrainer } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    in_progress: 0,
    resolved: 0,
    urgent: 0
  });

  const isStaffMember = isGymOwner || isStaff || isTrainer;

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (currentTenant?.id) {
      loadTickets();
    }
  }, [user, currentTenant, filters]);

  const loadTickets = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await supportTicketService?.getTickets(
        user?.id,
        currentTenant?.id,
        isStaffMember,
        filters
      );

      if (result?.success) {
        setTickets(result?.tickets || []);
        calculateStats(result?.tickets || []);
      } else {
        setError(result?.error || 'Failed to load tickets');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Load tickets error:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (ticketData) => {
    setStats({
      total: ticketData?.length,
      open: ticketData?.filter(t => t?.status === 'open')?.length,
      in_progress: ticketData?.filter(t => t?.status === 'in_progress')?.length,
      resolved: ticketData?.filter(t => t?.status === 'resolved')?.length,
      urgent: ticketData?.filter(t => t?.priority === 'urgent')?.length
    });
  };

  const handleCreateTicket = async (ticketData) => {
    try {
      const result = await supportTicketService?.createTicket(
        ticketData,
        user?.id,
        currentTenant?.id
      );

      if (result?.success) {
        setShowCreateModal(false);
        await loadTickets();
        
        if (result?.automatedResponse) {
          alert('Ticket created! An automated response has been generated.');
        }
      } else {
        alert(result?.error || 'Failed to create ticket');
      }
    } catch (err) {
      alert('An unexpected error occurred');
      console.error('Create ticket error:', err);
    }
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleStatusUpdate = async (ticketId, newStatus) => {
    try {
      const result = await supportTicketService?.updateTicketStatus(ticketId, newStatus);
      if (result?.success) {
        await loadTickets();
      }
    } catch (err) {
      console.error('Status update error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-heading font-bold text-foreground">
              AI Support Tickets
            </h1>
            <Button
              onClick={() => setShowCreateModal(true)}
              iconName="Plus"
              iconPosition="left"
            >
              New Ticket
            </Button>
          </div>
          <p className="text-muted-foreground">
            Intelligent ticket management with AI-powered routing and automated responses
          </p>
        </div>

        {/* Stats Cards */}
        <TicketStats stats={stats} />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Ticket List Section */}
          <div className="lg:col-span-2 space-y-4">
            <TicketFilters
              filters={filters}
              onFilterChange={handleFilterChange}
            />

            {loading ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="Loader2" size={32} className="animate-spin mx-auto mb-4 text-primary" />
                <p className="text-muted-foreground">Loading tickets...</p>
              </div>
            ) : error ? (
              <div className="bg-destructive/10 border border-destructive rounded-lg p-6 text-center">
                <Icon name="AlertCircle" size={32} className="mx-auto mb-4 text-destructive" />
                <p className="text-destructive font-medium">{error}</p>
                <Button
                  onClick={loadTickets}
                  variant="outline"
                  className="mt-4"
                  iconName="RefreshCw"
                >
                  Retry
                </Button>
              </div>
            ) : tickets?.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="Inbox" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  No Tickets Found
                </h3>
                <p className="text-muted-foreground mb-4">
                  {filters?.status || filters?.priority || filters?.category
                    ? 'Try adjusting your filters' :'Create your first support ticket to get started'}
                </p>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  iconName="Plus"
                >
                  Create Ticket
                </Button>
              </div>
            ) : (
              <TicketList
                tickets={tickets}
                onTicketClick={handleTicketClick}
                isStaff={isStaffMember}
              />
            )}
          </div>

          {/* AI Insights Panel */}
          <div className="lg:col-span-1">
            <AIInsightsPanel
              tickets={tickets}
              isStaff={isStaffMember}
            />
          </div>
        </div>
      </main>

      <MobileBottomNav />

      {/* Modals */}
      {showCreateModal && (
        <CreateTicketModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateTicket}
        />
      )}

      {selectedTicket && (
        <TicketDetailsModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onStatusUpdate={handleStatusUpdate}
          isStaff={isStaffMember}
          userId={user?.id}
        />
      )}
    </div>
  );
};

export default AISupportTicketSystem;